import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { HibajegyStatuszCimke, PrioritasCimke } from '../hibajelentes/Cimkek.tsx';
import { useProfilNevek } from '../hibajelentes/nevek.ts';
import { szuroIllik, szuroErtek, type HibajegySor, type SzuroAllapot } from '../hibajelentes/tipusok.ts';
import { useAktivIroda } from '../iroda/AktivIroda.tsx';
import { formatDatum } from '../lib/formaz.ts';
import { supabase } from '../lib/supabase.ts';
import { Betolto, Fokonyv, HibaUzenet, OldalFej, Ures, Valaszto } from '../ui/elemek.tsx';

const SZURO_CIMKE: Record<SzuroAllapot, string> = { nyitott: 'Nyitott', lezart: 'Lezárt', mind: 'Mind' };

export default function Hibajelentesek() {
  const { irodak, aktivId, jogok } = useAktivIroda();
  const [parameterek, setParameterek] = useSearchParams();
  const szuro = szuroErtek(parameterek.get('allapot'));
  const irodaSzuro = jogok.super ? parameterek.get('iroda') ?? '' : '';

  const lekeres = useQuery({
    queryKey: ['hibajegyek', 'lista'],
    queryFn: async () => {
      const { data, error } = await supabase.from('hibajegyek').select('*').order('created_at', { ascending: false }).limit(500);
      if (error) throw error;
      return data satisfies HibajegySor[];
    },
    refetchOnWindowFocus: true,
  });
  const nevek = useProfilNevek((lekeres.data ?? []).map((j) => j.bekuldo_id));

  function valt(kulcs: string, ertek: string) {
    const uj = new URLSearchParams(parameterek);
    if (ertek === '' || (kulcs === 'allapot' && ertek === 'nyitott')) uj.delete(kulcs); else uj.set(kulcs, ertek);
    setParameterek(uj, { replace: true });
  }

  // Nem super adminnál a fejléc irodaváltója dönt (mint a leadeknél); a super admin az irodaszűrőt használja.
  const lista = (lekeres.data ?? []).filter((j) => szuroIllik(szuro, j.statusz)
    && (jogok.super ? irodaSzuro === '' || j.office_id === irodaSzuro : j.office_id === aktivId));
  const irodaNev = (id: string) => irodak.find((i) => i.id === id)?.nev ?? '–';

  return (
    <>
      <OldalFej cim="Hibajelentések" leiras={jogok.super ? 'Minden iroda jelentése. A beküldő és az iroda az állapotváltásról e-mailt kap.' : jogok.vezeto ? 'Az iroda jelentései. Új hibát a lebegő „Hiba jelzése” gombbal küldhetsz.' : 'A saját jelentéseid. Új hibát a lebegő „Hiba jelzése” gombbal küldhetsz.'}>
        <div className="flex flex-wrap items-end gap-3">
          <div role="group" aria-label="Szűrő" className="flex gap-1 rounded-xl border border-vonal p-1">
            {(Object.keys(SZURO_CIMKE) as SzuroAllapot[]).map((k) => (
              <button key={k} type="button" aria-pressed={szuro === k} onClick={() => valt('allapot', k)}
                className={`min-h-9 rounded-[10px] px-3 text-sm transition ${szuro === k ? 'bg-felulet text-szoveg' : 'text-szoveg-halk hover:text-szoveg'}`}>
                {SZURO_CIMKE[k]}
              </button>
            ))}
          </div>
          {jogok.super && (
            <Valaszto label="Iroda" value={irodaSzuro} onChange={(e) => valt('iroda', e.target.value)}>
              <option value="">Mind</option>
              {irodak.map((i) => <option key={i.id} value={i.id}>{i.nev}</option>)}
            </Valaszto>
          )}
        </div>
      </OldalFej>

      <HibaUzenet hiba={lekeres.error} />
      {lekeres.isPending && <Betolto />}
      {lekeres.data && lista.length === 0 && (
        <Ures cim={szuro === 'nyitott' ? 'Nincs nyitott hibajelentés.' : szuro === 'lezart' ? 'Nincs lezárt hibajelentés.' : 'Még nincs hibajelentés.'}
          szoveg={szuro === 'nyitott' ? 'Ha valami nem működik, jelezd a jobb alsó sarokban lévő „Hiba jelzése” gombbal.' : undefined} />
      )}
      {lista.length > 0 && (
        <Fokonyv>
          <ul aria-label="Hibajelentések listája" className="divide-y divide-vonal">
            {lista.map((j) => (
              <li key={j.id} data-testid="hibajegy-sor">
                <Link to={`/hibajelentesek/${j.id}`} className="grid gap-1 px-4 py-3 hover:bg-felulet sm:grid-cols-[1fr_auto] sm:items-center sm:gap-6 sm:px-5">
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{j.cim}</span>
                    <span className="block truncate text-sm text-szoveg-halk">
                      {j.bekuldo_id ? nevek.get(j.bekuldo_id) ?? 'Munkatárs' : 'Törölt felhasználó'}
                      {jogok.super && <> · {irodaNev(j.office_id)}</>}
                      <> · <span className="szam">{formatDatum(j.created_at)}</span></>
                    </span>
                  </span>
                  <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <PrioritasCimke prioritas={j.prioritas} />
                    <HibajegyStatuszCimke statusz={j.statusz} />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </Fokonyv>
      )}
    </>
  );
}
